// hl-custom: custom file, not in stock Horizon -- Klaviyo marketing opt-in custom element for the cart (feature: marketing-optin)
import { Component } from '@theme/component';

const STORAGE_KEY = 'holivica:marketing-optin:subscribed';
const KLAVIYO_READY_TIMEOUT_MS = 5000;
const KLAVIYO_READY_POLL_MS = 200;
const KLAVIYO_API_REVISION = '2024-10-15';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Cart drawer marketing opt-in.
 *
 * Subscribes the email to a Klaviyo list via the public Client API; a
 * Klaviyo flow generates a unique single-use coupon and emails it.
 *
 * Persistence: a successful subscription writes {@link STORAGE_KEY} so the
 * form stays hidden across cart-drawer re-renders (Section Rendering API
 * morphs) and reloads in the same browser.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} form
 * @property {HTMLInputElement} checkbox
 * @property {HTMLElement} emailWrap
 * @property {HTMLInputElement} emailInput
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLElement} status
 * @property {HTMLElement} successState
 *
 * @extends {Component<Refs>}
 */
class MarketingOptin extends Component {
  requiredRefs = ['form', 'checkbox', 'emailWrap', 'emailInput', 'submitButton', 'status', 'successState'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    if (this.#hasSubscribed()) {
      this.#showSuccess();
      return;
    }
    // Fire-and-forget: never block render on Klaviyo, and swallow all errors
    // internally so the widget degrades gracefully if the onsite SDK is absent.
    this.#initKlaviyoIntegration();
  }

  /**
   * Fired when the Section Rendering API morphs this component's children
   * (e.g. every time the cart drawer is opened or a cart line changes).
   * The morph replaces the form/successState nodes with fresh server-rendered
   * defaults — form visible, success hidden — so we must re-apply our
   * "already subscribed" UI state here.
   */
  updatedCallback() {
    super.updatedCallback();
    if (this.#hasSubscribed()) {
      this.#showSuccess();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#activeFetch?.abort();
    this.#activeFetch = null;
  }

  handleCheckboxChange = () => {
    const { checkbox, emailWrap, emailInput } = this.refs;
    const expanded = checkbox.checked;
    emailWrap.hidden = !expanded;
    this.setAttribute('aria-expanded', String(expanded));
    if (expanded) {
      requestAnimationFrame(() => emailInput.focus());
    } else {
      this.#clearStatus();
    }
  };

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit = async (event) => {
    event.preventDefault();
    if (this.#activeFetch) return;

    const { emailInput, submitButton } = this.refs;
    const email = emailInput.value.trim();

    if (!EMAIL_RE.test(email)) {
      this.#showError(this.dataset.errorInvalidEmail || 'Invalid email');
      emailInput.focus();
      return;
    }

    const publicKey = this.dataset.publicKey;
    const listId = this.dataset.listId;
    if (!publicKey || !listId) {
      this.#showError(this.dataset.errorGeneric || 'Error');
      return;
    }

    this.#clearStatus();
    submitButton.disabled = true;
    this.setAttribute('data-loading', 'true');

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(publicKey)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            Accept: 'application/vnd.api+json',
            revision: KLAVIYO_API_REVISION,
          },
          signal: abortController.signal,
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Cart Drawer Opt-in',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email },
                  },
                },
              },
              relationships: {
                list: { data: { type: 'list', id: listId } },
              },
            },
          }),
        }
      );

      // Klaviyo returns 202 Accepted on success. Already-subscribed profiles
      // also return 202 — the welcome flow's "received 0 times" filter handles
      // dedupe so repeat subscribers don't get a second coupon.
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        console.error('[marketing-optin] Klaviyo error', response.status, body);
        throw new Error(`Klaviyo subscribe failed: ${response.status}`);
      }

      this.#markSubscribed();
      this.#showSuccess();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('[marketing-optin] Submit error', error);
      this.#showError(this.dataset.errorGeneric || 'Error');
    } finally {
      submitButton.disabled = false;
      this.removeAttribute('data-loading');
      this.#activeFetch = null;
    }
  };

  /**
   * Hides the widget for visitors who already have a Klaviyo profile —
   * they're filtered out of the welcome-coupon flow anyway, so showing
   * the form would be misleading. Mirrors Klaviyo's native form option
   * "Don't show to existing Klaviyo profiles".
   *
   * Note: we intentionally do NOT fire a custom `track()` view event here.
   * Klaviyo's onsite JS silently drops `track()` calls from anonymous (un-
   * cookied) browsers — and identified browsers are exactly the ones we're
   * removing the widget for — so a view metric would always be empty.
   * Raw signup counts come from the server-side `Subscribed to List` event
   * fired by the Client Subscriptions API.
   */
  async #initKlaviyoIntegration() {
    const klaviyo = await this.#waitForKlaviyo();
    if (!klaviyo) return;

    try {
      if (typeof klaviyo.isIdentified === 'function') {
        const identified = await klaviyo.isIdentified();
        if (identified) {
          this.remove();
        }
      }
    } catch (error) {
      console.warn('[marketing-optin] klaviyo.isIdentified failed', error);
    }
  }

  /**
   * Resolves with `window.klaviyo` once available, or `null` if the onsite
   * script never loads within the timeout.
   * @returns {Promise<any | null>}
   */
  #waitForKlaviyo() {
    if (window.klaviyo) return Promise.resolve(window.klaviyo);
    return new Promise((resolve) => {
      const start = Date.now();
      const intervalId = setInterval(() => {
        if (window.klaviyo) {
          clearInterval(intervalId);
          resolve(window.klaviyo);
        } else if (Date.now() - start >= KLAVIYO_READY_TIMEOUT_MS) {
          clearInterval(intervalId);
          resolve(null);
        }
      }, KLAVIYO_READY_POLL_MS);
    });
  }

  #hasSubscribed() {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  #markSubscribed() {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // Non-fatal: success UI still shows for this session.
    }
  }

  #showSuccess() {
    const { form, successState } = this.refs;
    form.hidden = true;
    successState.hidden = false;
  }

  /** @param {string} message */
  #showError(message) {
    const { status } = this.refs;
    status.textContent = message;
    status.hidden = false;
    status.dataset.state = 'error';
  }

  #clearStatus() {
    const { status } = this.refs;
    status.textContent = '';
    status.hidden = true;
    delete status.dataset.state;
  }
}

if (!customElements.get('marketing-optin-component')) {
  customElements.define('marketing-optin-component', MarketingOptin);
}
